export type CardPaymentFieldErrors = {
  nationalId?: string;
  cardName?: string;
  cardNumber?: string;
  expiry?: string;
  cvc?: string;
};

export type CardPaymentInput = {
  cardName: string;
  cardNumber: string;
  expiry: string;
  cvc: string;
  nationalId?: string;
  requireNationalId?: boolean;
};

function digitsOnly(value: string): string {
  return value.replace(/\D+/g, '');
}

/** Luhn checksum — standard card number validation. */
export function luhnCheck(cardNumber: string): boolean {
  const digits = digitsOnly(cardNumber);
  if (digits.length < 13 || digits.length > 19) {
    return false;
  }
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let n = parseInt(digits[i], 10);
    if (Number.isNaN(n)) {
      return false;
    }
    if (alt) {
      n *= 2;
      if (n > 9) {
        n -= 9;
      }
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

/**
 * Brand-aware length + Luhn. Rejects all-identical digit strings and lengths
 * that no major network issues.
 */
export function isValidLegalCardNumber(raw: string): boolean {
  const digits = digitsOnly(raw);
  if (digits.length < 13 || digits.length > 19) {
    return false;
  }
  if (/^(\d)\1+$/.test(digits)) {
    return false;
  }
  // American Express
  if (/^3[47]/.test(digits)) {
    return digits.length === 15 && luhnCheck(digits);
  }
  // Visa
  if (/^4/.test(digits)) {
    return (digits.length === 13 || digits.length === 16 || digits.length === 19) && luhnCheck(digits);
  }
  // Mastercard (incl. 2-series)
  if (/^(5[1-5]|2[2-7])/.test(digits)) {
    return digits.length === 16 && luhnCheck(digits);
  }
  // Discover
  if (/^(6011|65|64[4-9])/.test(digits)) {
    return digits.length === 16 && luhnCheck(digits);
  }
  // Diners Club
  if (/^(30[0-5]|36|38)/.test(digits)) {
    return (digits.length === 14 || digits.length === 16) && luhnCheck(digits);
  }
  // Other networks: require Luhn within ISO/IEC 7812 length range
  return luhnCheck(digits);
}

/** Israeli national ID — exactly 9 digits (demo validation; no checksum gate). */
export function isValidIsraeliNationalId(raw: string): boolean {
  const digits = digitsOnly(raw);
  return /^\d{9}$/.test(digits);
}

export function cardExpiryIsInPast(mm: number, yy: number, now: Date = new Date()): boolean {
  const fullYear = 2000 + yy;
  const endOfMonth = new Date(fullYear, mm, 0, 23, 59, 59, 999);
  return endOfMonth.getTime() < now.getTime();
}

export type CardPaymentMessages = {
  errorNationalId: string;
  errorName: string;
  errorNameInvalid: string;
  errorNumber: string;
  errorNumberLuhn: string;
  errorExpiry: string;
  errorExpiryPast: string;
  errorCvc: string;
};

/** Validate credit-card fields; returns per-field error messages (empty object = valid). */
export function validateCardPayment(
  input: CardPaymentInput,
  messages: CardPaymentMessages,
  now: Date = new Date(),
): CardPaymentFieldErrors {
  const errors: CardPaymentFieldErrors = {};

  if (input.requireNationalId) {
    if (!isValidIsraeliNationalId(input.nationalId ?? '')) {
      errors.nationalId = messages.errorNationalId;
    }
  }

  const name = input.cardName.trim();
  if (!name) {
    errors.cardName = messages.errorName;
  } else if (
    name.length < 2 ||
    !/^[\u0590-\u05FFa-zA-Z][\u0590-\u05FFa-zA-Z\s'.-]*$/.test(name)
  ) {
    errors.cardName = messages.errorNameInvalid;
  }

  const number = digitsOnly(input.cardNumber);
  if (number.length < 13 || number.length > 19) {
    errors.cardNumber = messages.errorNumber;
  } else if (!isValidLegalCardNumber(number)) {
    errors.cardNumber = messages.errorNumberLuhn;
  }

  const expiryDigits = digitsOnly(input.expiry);
  if (expiryDigits.length !== 4) {
    errors.expiry = messages.errorExpiry;
  } else {
    const mm = parseInt(expiryDigits.slice(0, 2), 10);
    const yy = parseInt(expiryDigits.slice(2), 10);
    if (!(mm >= 1 && mm <= 12)) {
      errors.expiry = messages.errorExpiry;
    } else if (cardExpiryIsInPast(mm, yy, now)) {
      errors.expiry = messages.errorExpiryPast;
    }
  }

  const cvcDigits = digitsOnly(input.cvc);
  if (cvcDigits.length < 3 || cvcDigits.length > 4) {
    errors.cvc = messages.errorCvc;
  }

  return errors;
}

export function cardPaymentIsValid(errors: CardPaymentFieldErrors): boolean {
  return Object.keys(errors).length === 0;
}

/** PayPlus sandbox “successful charge” Mastercard (keep charges ≤ ₪5). */
export const PAYPLUS_TEST_CARD_DIGITS = '5326140280779844';

/** Amount forced onto the booking when the PayPlus test card is used in checkout. */
export const PAYPLUS_TEST_CHARGE_NIS = 1;

export function isPayplusTestCardNumber(raw: string): boolean {
  return digitsOnly(raw) === PAYPLUS_TEST_CARD_DIGITS;
}

